import { useNavigate, useParams } from "react-router-dom";
import { JOURNEY_DETAILS_STEPS } from "../constants";
import { JourneyStepValue, ValidityState } from "../constants/JourneyDetails";
import { useJourneyDynamicDetails } from "../Pages/JourneyDetails/context";

const DEFAULT_STEP = 0;

export default function useStepFlow() {
  const { validityState } = useJourneyDynamicDetails();
  const [validity, setValidity] = validityState;
  const { searchId, ticketId, step: stepParam } = useParams();
  const navigate = useNavigate();
  const stepIndex = JOURNEY_DETAILS_STEPS.findIndex((s) => s.value === stepParam);
  const currentStep = stepIndex >= 0 ? stepIndex : DEFAULT_STEP;

  const navigateToStep = (step: number, replace = false) => {
    const value = JOURNEY_DETAILS_STEPS[step]?.value;
    if (!value) return;
    navigate(`/travel/tickets/${searchId}/ticket/${ticketId}/${value}`, { replace });
  };

  const goToStep = (step: number) => {
    const stepConfiguration = JOURNEY_DETAILS_STEPS.find((_, index) => index === step);
    const nextMovementPossible = stepConfiguration?.canEnter?.(validity);
    if (!nextMovementPossible || step === currentStep) return;
    navigateToStep(step);
  };
  const validateStep = (step: JourneyStepValue) => {
    const newValidity = {
      ...validity,
      [step]: true,
    };
    setValidity(newValidity);
    return newValidity;
  };
  const invalidateStep = (step: JourneyStepValue) => {
    const newValidity = {
      ...validity,
      [step]: false,
    };
    setValidity(newValidity);
    return newValidity;
  };
  const canEnterStep = (step: number, overrideValidity?: ValidityState): boolean => {
    const config = JOURNEY_DETAILS_STEPS.find((s, index) => index === step);
    return config?.canEnter?.(overrideValidity ?? validity) ?? false;
  };

  const goToNextStep = (overrideValidity?: ValidityState) => {
    const nextStep = JOURNEY_DETAILS_STEPS[currentStep + 1];
    if (!nextStep) return;
    if (!canEnterStep(currentStep + 1, overrideValidity)) return;
    navigateToStep(currentStep + 1);
  };
  const goToPrevStep = () => {
    const prevStep = JOURNEY_DETAILS_STEPS?.[currentStep - 1];
    if (!prevStep) return;
    navigateToStep(currentStep - 1);
  };

  const ensureStepAllowed = () => {
    if (!canEnterStep(currentStep)) navigateToStep(DEFAULT_STEP, true);
  };

  const isStepAllowed = (step: number) => canEnterStep(step);

  return { goToStep, currentStep, validateStep, invalidateStep, isStepAllowed, goToNextStep, goToPrevStep, ensureStepAllowed };
}
